import { AuditPromiseClient } from '@trustero/trustero-api-web/lib/audit/audit_grpc_web_pb'
import React, {
  ChangeEvent,
  FormEventHandler,
  MouseEvent,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Range } from 'react-date-range'
import log from 'loglevel'
import FormControl from 'react-bootstrap/FormControl'
import {
  AuditRecord,
  SaveAuditRequest,
} from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { ComplianceFrameworkOption } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { toast } from 'react-toastify'
import {
  useHideModal,
  useIsShowModal,
  useModalState,
} from 'src/Modal/ModalStateContext'
import { useCurrentFormAuditId } from 'src/context/FormContext/CurrentFormAuditContext'
import { DATE_FORMATS } from 'src/Utils/dateConstants'
import { ModalForm, ModalFormId, ModalFormIdQueryParam } from '../ModalForm'
import { useAuthorizedGrpcClient } from '../../../adapter'
import { useGrpcRevalidateByMethod } from '../../async'
import { FieldLabel, WarningP } from '../ModalForm.styles'
import { TrusteroDateRange } from '../../Reusable/TrusteroDateRange'
import {
  dateToTimestamp,
  formatTimestamp,
  hasDatePassed,
} from '../../../Utils/formatDate'
import { useAudit } from '../../async/model/audit'
import { ComplianceFrameworksDropdown } from '../Models/Dropdowns/ComplianceFrameworksDropdown'
import { getEndDate, getStartDate } from './auditUtils'

enum AuditFormFields {
  COMPLIANCE_FRAMEWORK = 'framework',
  COMPLIANCE_FRAMEWORKS = 'frameworks',
  AUDIT_NAME = 'name',
  DATE_RANGE = 'dateRange',
}
export const AuditFormQueryParams = {
  MODAL_FORM_ID: ModalFormIdQueryParam,
  AUDIT_ID: 'auditId',
}

/**
 * Framework combinations are valid only if they contain all the same type of framework:
 * - Design & Implementation / Point-in-Time (SOC 2 Type 1)
 * - Operating Effectiveness / Interval (SOC 2 Type 2, ISO 27001)
 */
const isValidFrameworksCombination = (
  frameworks: ComplianceFrameworkOption[],
): boolean => {
  const containsIntervalFramework = frameworks.some((framework) =>
    framework.getIsInterval(),
  )
  const containsPointInTimeFramework = frameworks.some(
    (framework) => !framework.getIsInterval(),
  )

  return containsIntervalFramework !== containsPointInTimeFramework
}

export const AddOrEditAuditForm = (): JSX.Element => {
  const { activeModal } = useModalState()
  const { auditId } = useCurrentFormAuditId()
  const auditClient = useAuthorizedGrpcClient(AuditPromiseClient)
  const grpcRevalidateByMethod = useGrpcRevalidateByMethod()
  const [validateFormFramework, setValidateFormFramework] = useState(false)
  const [validateFormName, setValidateFormName] = useState(false)
  const [validateFormDate, setValidateFormDate] = useState(false)
  const [frameworks, setFrameworks] = useState<
    Array<ComplianceFrameworkOption>
  >([])
  const [name, setName] = useState('')
  const [dateRange, setDateRange] = useState<Range>({
    startDate: new Date(),
    endDate: getEndDate(),
  })
  const [calendarDateRange, setCalendarDateRange] = useState<Range>({
    startDate: getStartDate(),
    endDate: new Date(),
  })
  const [isInterval, setIsInterval] = useState(true)
  const [busy, setBusy] = useState(false)

  const isValid = useMemo(() => {
    return (
      isValidFrameworksCombination(frameworks) &&
      name.trim()?.length &&
      dateRange
    )
  }, [frameworks, name, dateRange])

  const isEdit = activeModal === ModalFormId.EDIT_AUDIT
  // We only want the audit IF we are editing
  const audit = useAudit(isEdit ? auditId : '')

  useEffect(() => {
    setName(audit.data?.getName() || '')
    setFrameworks(audit.data?.getComplianceFrameworkOptionsList() || [])
    setIsInterval(audit.data?.getIsInterval() || false)
    setDateRange({
      startDate: audit.data?.getStartDate()?.toDate() || new Date(),
      endDate: audit.data?.getEndDate()?.toDate() || getEndDate(),
    })
    setCalendarDateRange({
      startDate: audit.data?.getStartDate()?.toDate() || getStartDate(),
      endDate: audit.data?.getEndDate()?.toDate() || new Date(),
    })
  }, [audit.data])

  const onHide = () => {
    setFrameworks([])
    setName('')
    setDateRange({
      startDate: new Date(),
      endDate: getEndDate(),
    })
    setCalendarDateRange({
      startDate: getStartDate(),
      endDate: new Date(),
    })
    setValidateFormFramework(false)
    setValidateFormName(false)
    setValidateFormDate(false)
  }
  const show = useIsShowModal([ModalFormId.ADD_AUDIT, ModalFormId.EDIT_AUDIT])
  const hide = useHideModal({ onHide })
  const formSet = useMemo(() => {
    return {
      [AuditFormFields.COMPLIANCE_FRAMEWORKS]:
        (complianceFramework: ComplianceFrameworkOption) =>
        (_: ChangeEvent<HTMLElement>) => {
          const selectedFrameworks = [...frameworks]
          const index = selectedFrameworks.findIndex(
            (item) => item.getId() === complianceFramework.getId(),
          )

          // Remove the selected framework if it is already present. Otherwise, add it.
          index !== -1
            ? selectedFrameworks.splice(index, 1)
            : selectedFrameworks.push(complianceFramework)

          setValidateFormFramework(true)
          setFrameworks(selectedFrameworks)
          if (isValidFrameworksCombination(selectedFrameworks)) {
            setIsInterval(selectedFrameworks[0]?.getIsInterval())
          }
        },

      [AuditFormFields.COMPLIANCE_FRAMEWORK]:
        (framework: ComplianceFrameworkOption) =>
        (e: MouseEvent<HTMLElement>) => {
          e.preventDefault()

          setValidateFormFramework(true)
          setFrameworks([framework])
          setIsInterval(framework.getIsInterval())
        },

      [AuditFormFields.AUDIT_NAME]: ({
        target,
      }: ChangeEvent<HTMLInputElement>) => {
        setValidateFormName(true)
        setName(target.value)
      },

      [AuditFormFields.DATE_RANGE]: ({
        target,
      }: ChangeEvent<HTMLTextAreaElement>) => {
        setValidateFormDate(true)
        setDateRange(target)
      },
    }
  }, [frameworks])

  const onSubmit: FormEventHandler<HTMLFormElement> = useMemo(() => {
    return async (e) => {
      e.preventDefault()
      setBusy(true)
      setValidateFormFramework(true)
      setValidateFormName(true)
      setValidateFormDate(true)
      if (isValid) {
        const audit = new AuditRecord()
        name && audit.setName(name.trim())
        frameworks && audit.setComplianceFrameworkOptionsList(frameworks)
        audit.setIsInterval(isInterval)
        const endTime = dateToTimestamp(
          isInterval ? dateRange.endDate : calendarDateRange.endDate,
        )
        audit.setEndDate(endTime)
        const startTime = dateToTimestamp(
          isInterval ? dateRange.startDate : calendarDateRange.startDate,
        )
        audit.setStartDate(startTime)
        // We only want to set the audit id if we are editing
        audit.setId(isEdit ? auditId : '')

        const request = new SaveAuditRequest()
          .setAudit(audit)
          .setIncludeControls(true)

        try {
          await auditClient.saveAudit(request)
          await grpcRevalidateByMethod(AuditPromiseClient.prototype.getAudits)
          // Close state and close modal
          hide()
        } catch (e) {
          toast.error(<div>{'There was a problem saving the audit.'}</div>)
          log.error('Error in AddOrEditAuditForm', e)
        } finally {
          setBusy(false)
        }
      }
    }
  }, [
    isValid,
    name,
    frameworks,
    isInterval,
    dateRange.endDate,
    dateRange.startDate,
    calendarDateRange.endDate,
    calendarDateRange.startDate,
    isEdit,
    auditId,
    auditClient,
    grpcRevalidateByMethod,
    hide,
  ])

  const endDate = audit.data?.getEndDate()
  const isPastAudit = endDate && hasDatePassed(endDate)
  const endDay = endDate ? formatTimestamp(endDate, DATE_FORMATS.MONTH_DAY) : ''

  return (
    <ModalForm
      show={show}
      hide={hide}
      formId={ModalFormId.ADD_AUDIT}
      title={isEdit ? 'Edit Audit' : 'Add Audit'}
      submitText={isEdit ? 'Update Audit' : 'Submit'}
      enforceFocus={false}
      busy={busy}
    >
      <form id={ModalFormId.ADD_AUDIT} onSubmit={onSubmit}>
        {isPastAudit && endDay && (
          <WarningP>
            The end date of this audit was {endDay}. Continuing to edit it may
            be confusing as new evidence won&apos;t appear, etc.
          </WarningP>
        )}
        <fieldset>
          <FieldLabel
            validateForm={validateFormFramework}
            isInvalid={
              !frameworks.length || !isValidFrameworksCombination(frameworks)
            }
            message={
              !frameworks.length
                ? 'Please select a Compliance Framework'
                : "Can't combine these audit types together."
            }
          >
            Compliance Framework
            <ComplianceFrameworksDropdown
              readOnly={isEdit}
              selectedComplianceFrameworks={frameworks}
              onComplianceFrameworkSelected={
                formSet[AuditFormFields.COMPLIANCE_FRAMEWORKS]
              }
            />
          </FieldLabel>
        </fieldset>
        <fieldset>
          <FieldLabel
            validateForm={validateFormName}
            isInvalid={!name.trim()}
            message="Please add a name"
          >
            Name
            <FormControl
              name={AuditFormFields.AUDIT_NAME}
              value={name}
              onChange={formSet[AuditFormFields.AUDIT_NAME]}
              placeholder="Audit name"
            />
          </FieldLabel>
        </fieldset>
        <fieldset>
          <FieldLabel
            validateForm={validateFormDate}
            isInvalid={!dateRange.startDate || !dateRange.endDate}
            message="Please select a date"
          >
            {isInterval ? (
              <>
                Date Range
                <br />
                <TrusteroDateRange
                  single={false}
                  range={dateRange}
                  rangeSet={(p) => {
                    setDateRange(p)
                    formSet[AuditFormFields.DATE_RANGE]
                  }}
                />
              </>
            ) : (
              <>
                Date for point in time audit
                <br />
                <TrusteroDateRange
                  single={true}
                  range={calendarDateRange}
                  rangeSet={(p) => {
                    setCalendarDateRange(p)
                    formSet[AuditFormFields.DATE_RANGE]
                  }}
                />
              </>
            )}
          </FieldLabel>
        </fieldset>
      </form>
    </ModalForm>
  )
}
